// @ds/data/auditChildScope — merges a child table's audit rows into a
// parent's activity feed.
//
// Some entities have "children" that don't warrant their own audit trigger
// scope in the feed — e.g. `transaction_files` rows belong to a transaction,
// but the transaction's AKTYWNOŚĆ tab should only ever show a verdict
// (`review_status` changed), never every INSERT/DELETE/classification edit
// on the file. This module is the pure merge: given the parent's own audit
// rows and the child's (already fetched) audit rows, filter the child rows
// down to the scope the caller cares about, then interleave both lists into
// one chronologically-sorted, limit-trimmed feed.
//
// `transaction_files` is the first consumer (#271); `invoice_line_items`
// could adopt the same `AuditChildScope` shape without touching this module.

import type { TFunction } from "i18next";
import type { AuditLogRow } from "@ds/data/auditLogSchema";

/**
 * A child audit row. `auditLogSchema`'s default select omits `entity_id` (the
 * parent feed already knows its own id), so consumers fetch it additively via
 * `Query.also` — it is what maps an audit row back to the child record it
 * describes, and the only handle left when that record has been hard-deleted.
 */
export interface ChildAuditLogRow extends AuditLogRow {
  entity_id: number;
  /**
   * The row's own `entity_type`. A feed may weave in more than one child table
   * (a transaction carries both `transaction_file` verdicts and
   * `transaction_transport` leg edits); this is what maps a merged row back to
   * the scope that admitted it, for label + column-name resolution.
   */
  entity_type: string;
}

/**
 * Description of a child table whose audit rows weave into a parent's feed.
 * Generic — `transaction_files` is the first consumer, `invoice_line_items`
 * could join without changes to this module.
 */
export interface AuditChildScope<TChild extends { id: number } = { id: number }> {
  /** Refine resource holding the child rows (e.g. "transaction_files"). */
  resource: string;
  /** Child FK column pointing at the parent id (e.g. "transaction_id"). */
  fk: string;
  /** entity_type the audit_trigger records for this table. */
  entityType: string;
  /** SELECT for the child rows — must include "id". */
  select: string;
  /** Only UPDATEs touching one of these fields enter the feed. */
  fields: readonly string[];
  /**
   * Admit INSERT and DELETE rows too — the child record appearing or
   * disappearing is itself feed-worthy. Transport legs are hard-created and
   * hard-deleted (TransportSetupModal's per-leg create/delete pipeline), so
   * "leg added" / "leg removed" exist ONLY as INSERT / DELETE rows. Off by
   * default: `transaction_files` wants verdicts, not upload/delete churn.
   */
  lifecycle?: boolean;
  /** Entry label. `child` is undefined when the child row no longer exists. */
  label: (row: ChildAuditLogRow, child: TChild | undefined, t: TFunction) => string;
}

/**
 * A scope with its child-row type erased, so one feed can carry several scopes
 * over different child tables in a single array. Build these with
 * `defineAuditChildScope` — it keeps the `label` callback typed at the
 * definition site, which is the only place that knows the child's shape.
 */
export type AnyAuditChildScope = AuditChildScope<{ id: number }>;

/**
 * Declare a child scope, type-checking `label` against `TChild`, then widen it
 * for storage alongside scopes over other tables.
 *
 * The widening is sound in use because a scope only ever meets its OWN child
 * rows: `ActivityBody` fetches `scope.resource` with `scope.select` and hands
 * the result back to that same `scope.label`. It is not sound structurally
 * (`label` is contravariant in `child`), hence the cast lives here — once —
 * instead of at every call site.
 */
export function defineAuditChildScope<TChild extends { id: number }>(
  scope: AuditChildScope<TChild>,
): AnyAuditChildScope {
  return scope as AnyAuditChildScope;
}

/** An audit row tagged with whether it came from the parent or a child. */
export interface ScopedAuditRow {
  row: AuditLogRow;
  scope: "self" | "child";
}

/**
 * Keep only the child audit rows a scope admits into the feed: UPDATEs whose
 * `changed_fields` touch at least one of `fields`. INSERT, DELETE, UPDATEs
 * that miss every field, and rows with `changed_fields: null` are dropped.
 *
 * With `opts.lifecycle`, INSERT and DELETE rows are admitted as well — the
 * child record appearing or disappearing is itself the event. The UPDATE rule
 * is unchanged either way, so an UPDATE touching no scoped field still drops.
 */
export function selectChildAuditRows(
  rows: readonly AuditLogRow[],
  fields: readonly string[],
  opts: { lifecycle?: boolean } = {},
): AuditLogRow[] {
  return rows.filter((row) => {
    if (row.action !== "UPDATE") return opts.lifecycle === true;
    return (row.changed_fields ?? []).some((f) => fields.includes(f));
  });
}

/**
 * Merge the parent's own audit rows and the (already filtered) child audit
 * rows into one list, sorted descending by (created_at, id) — the same
 * order as the `ActivityBody` sorter (`sharedSections.Bodies.tsx:94-99`) —
 * and trimmed to `limit`.
 */
export function mergeScopedAuditRows(
  self: readonly AuditLogRow[],
  child: readonly AuditLogRow[],
  limit: number,
): ScopedAuditRow[] {
  const merged: ScopedAuditRow[] = [
    ...self.map((row) => ({ row, scope: "self" as const })),
    ...child.map((row) => ({ row, scope: "child" as const })),
  ];

  merged.sort((a, b) => {
    const byCreatedAt = b.row.created_at.localeCompare(a.row.created_at);
    if (byCreatedAt !== 0) return byCreatedAt;
    return b.row.id - a.row.id;
  });

  return merged.slice(0, limit);
}
